import { kArtifactUpdateVersionPath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { ApiError } from '@/util/errors/api-error'
import { UpdateArtifactVersionRequest, ArtifactVersionRead, ArtifactVersionReadSchema } from '@/artifacts/schemas'

export async function updateArtifactVersion(request: UpdateArtifactVersionRequest): Promise<ArtifactVersionRead> {
  const { artifactVersionId, content, parentVersionId } = request
  const sentry_transaction_id = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error("Could not retrieve user's authentication token.")

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentry_transaction_id,
        Authorization: `Bearer ${token}`,
      },
      method: 'PUT',
      body: JSON.stringify({
        content,
        parentVersionId: parentVersionId || artifactVersionId,
      }),
    }

    const apiUrl = kPaxtonAppApiRESTBaseUrl() + kArtifactUpdateVersionPath.replace('{artifactVersionId}', artifactVersionId)
    const response = await fetch(apiUrl, requestOptions)
    const data = await response.json()

    if (!response.ok) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })

        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`), {
          extra: {
            requestUrl: apiUrl,
            requestOptions: requestOptions,
            response: JSON.stringify(response),
            data: JSON.stringify(data),
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new ApiError(response.status, `Failed to update artifact version: ${response.status}, detail: ${JSON.stringify(data.detail)}`)
    }

    // Parse so timestamps (created_at/updated_at) come back as epoch ms — callers store and compare
    // these, and a raw datetime string would break those comparisons. Fall back to the raw payload
    // on an unexpected shape so a validation surprise can't fail an otherwise-successful save; the
    // same schema already parses versions from conversation-load and the websocket, so this holds.
    const parsed = ArtifactVersionReadSchema.safeParse(data)
    if (parsed.success) return parsed.data
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: sentry_transaction_id })
      Sentry.captureException(new Error('updateArtifactVersion response failed schema validation'), {
        extra: { issues: JSON.stringify(parsed.error.issues) },
      })
    })
    return data as ArtifactVersionRead
  } catch (error) {
    if (!(error instanceof ApiError)) {
      // Skip api errors already captured above
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(error, {
          extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled },
        })
      })
    }
    throw error
  }
}
